import React, { MouseEvent, ReactNode, useEffect, useState } from 'react'

import { ThemeProvider, useMediaQuery, useTheme } from '@mui/material'
import html2canvas from 'html2canvas'

import { useSnackbarActions } from 'redux/actions/Snackbar'
import * as UserEvents from 'redux/api/internal/UserEvents'

import { TDashboard } from 'types/GlobalDashboard'
import { SnackbarMessage } from 'types/GlobalSnackbar'
import { Action } from 'types/GlobalUserEvents'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'

import ToggleButton from 'components_new/molecules/ToggleButton'

import { useGridRef } from 'components_new/templates/PageLayout'

import { getTheme } from 'themes'

import Desktop from './desktop'
import Mobile from './mobile'

interface ExportDialogProps {
  dashboard: TDashboard | null
  onClose: () => void
  onOpen: () => void
  open: boolean
}

export const IGNORE_ON_ALL_EXPORT = 'IGNORE_ON_ALL_EXPORT'
export const IGNORE_ON_TRANSPARENT_EXPORT = 'IGNORE_ON_TRANSPARENT_EXPORT'

const ExportDialog = (props: ExportDialogProps) => {
  const { dashboard, onClose, open } = props

  const { t } = useTranslation()
  const { toggleSnackbarOpen } = useSnackbarActions()
  const gridRef = useGridRef()

  const [fileType, setFileType] = useState<'png' | 'jpeg'>('png')
  const [scale, setScale] = useState<number>(1)

  const handleClose = () => {
    onClose()
  }

  const handleOpen = () => {
    onClose()
  }

  const downloadImage = (element: HTMLElement) => {
    const removedChildren: (HTMLTextAreaElement | HTMLParagraphElement)[][] = []

    // Replace textareas cause line break does not work.
    element.querySelectorAll('textarea').forEach((textarea) => {
      if (textarea.firstChild) {
        const div = document.createElement('div')

        while (textarea.firstChild) {
          div.appendChild(textarea.removeChild(textarea.firstChild))
        }
        removedChildren.push([div, textarea])

        div.style.overflowWrap = 'anywhere'

        const parent = textarea.parentNode

        if (parent) {
          parent.replaceChild(div, textarea)
        }
      }
    })

    html2canvas(element, {
      allowTaint: true,
      backgroundColor: fileType === 'png' ? null : '#f8fafc', // grey.100
      useCORS: true,
      logging: false,
      scale: scale,
      ignoreElements:
        fileType === 'png'
          ? (element) => {
              return (
                element.id === IGNORE_ON_ALL_EXPORT ||
                element.id === IGNORE_ON_TRANSPARENT_EXPORT
              )
            }
          : fileType === 'jpeg'
            ? (element) => {
                return element.id === IGNORE_ON_ALL_EXPORT
              }
            : undefined
    })
      .then((canvas) => {
        const downloadUrl = canvas.toDataURL(`image/${fileType}`)
        const link = document.createElement('a')

        link.href = downloadUrl
        link.download = `${dashboard?.title}.${fileType}`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // Append textareas again
        removedChildren.forEach(([div, textarea]) => {
          while (div.firstChild) {
            textarea.appendChild(div.removeChild(div.firstChild))
          }

          const parent = div.parentNode

          if (parent) {
            parent.replaceChild(textarea, div)
          }
        })

        toggleSnackbarOpen(SnackbarMessage.FileExported)
      })
      .catch(() => {
        // Append textareas again
        removedChildren.forEach(([div, textarea]) => {
          while (div.firstChild) {
            textarea.appendChild(div.removeChild(div.firstChild))
          }

          const parent = div.parentNode

          if (parent) {
            parent.replaceChild(textarea, div)
          }
        })
      })
  }

  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)

  useEffect(() => {
    if (gridRef && gridRef.current)
      setWidth(gridRef.current.offsetWidth * scale)
    if (gridRef && gridRef.current)
      setHeight(gridRef.current.offsetHeight * scale)
  }, [scale, gridRef?.current])

  /*-- dialog / drawer --*/
  return (
    <ThemeProvider theme={getTheme('light')}>
      <ContentWrapper
        onOpen={handleOpen}
        onClose={handleClose}
        open={open}
        title={t('global', 'export')}
      >
        <Box>
          <Text variant="subtitle2" gutterBottom>
            {t('global', 'variant')}
          </Text>
          <ToggleButton
            color={'primary'}
            exclusive={true}
            fullWidth={true}
            items={[
              {
                title: t('global', 'transparent'),
                iconName: 'PhotoLibraryOutlined',
                value: 'png'
              },
              {
                title: t('global', 'background'),
                iconName: 'PhotoLibrary',
                value: 'jpeg'
              }
            ]}
            value={fileType}
            onChange={(
              event: MouseEvent<HTMLElement>,
              value: 'png' | 'jpeg'
            ) => {
              if (!value) {
                return
              }
              setFileType(value)
            }}
          />
          <Text color="text.secondary" variant="caption">
            {t('global', 'format')}: .{fileType}
          </Text>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Text variant="subtitle2" gutterBottom>
            {t('global', 'quality')}
          </Text>
          <ToggleButton
            color={'primary'}
            exclusive={true}
            fullWidth={true}
            items={[
              { title: t('global', 'low'), value: 0.5 },
              { title: t('global', 'medium'), value: 1 },
              { title: t('global', 'high'), value: 2 }
            ]}
            value={scale}
            onChange={(event: MouseEvent<HTMLElement>, value: number) => {
              if (!value) {
                return
              }
              setScale(value)
            }}
          />
          <Text color="text.secondary" variant="caption">
            {t('global', 'resolution')}: {width} x {height}{' '}
            {t('global', 'pixels')}
          </Text>
        </Box>
        <Button
          fullWidth={true}
          onClick={() => {
            if (gridRef && gridRef.current) {
              if (!dashboard) return

              UserEvents.log({
                action: Action.DASHBOARD_IMAGE_EXPORTED,
                context: null,
                dashboard_id: dashboard.id,
                dashboard_group_id: dashboard.dashboard_group_id
              })

              downloadImage(gridRef.current)
            }
          }}
          startIcon={<Icon name="Download" />}
          sx={{ mt: 2 }}
        >
          {t('global', 'export')}
        </Button>
      </ContentWrapper>
    </ThemeProvider>
  )
}

interface ContentWrapperProps {
  children: ReactNode
  onClose: () => void
  onOpen: () => void
  open: boolean
  title: string
}

const ContentWrapper = (props: ContentWrapperProps) => {
  const { children, onClose, onOpen, open, title } = props

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('mobile'), {
    noSsr: true
  })

  return isMobile ? (
    <Mobile onClose={onClose} onOpen={onOpen} open={open} title={title}>
      {children}
    </Mobile>
  ) : (
    <Desktop onClose={onClose} open={open} title={title}>
      {children}
    </Desktop>
  )
}

export default ExportDialog
